import { useState, type ReactNode } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Home, Compass, Fingerprint, GraduationCap, User, MessageCircle, Shield,
  Briefcase, Image, Sparkles, Rocket, LayoutGrid, ListChecks, Search,
} from "lucide-react";
import WeKitLogo from "@/components/WeKitLogo";
import MentorPanel from "@/components/shell/MentorPanel";

/**
 * WCC360 v2 app shell.
 *
 * Navigation is deliberately five areas — everything else lives inside one of
 * them. Presentation only: no route guards, no data, no business logic.
 */
export const SHELL_AREAS = [
  { to: "/home", label: "Home", icon: Home, match: ["/home"] },
  { to: "/hub/journey", label: "Journey", icon: Compass, match: ["/hub/journey", "/journey", "/next-steps", "/vision-board", "/command-center", "/growth-plan", "/goals", "/challenges", "/reflect"] },
  { to: "/hub/dna", label: "DNA™", icon: Fingerprint, match: ["/hub/dna", "/report", "/skills", "/card", "/clarity-card", "/portfolio", "/second-opinion", "/assessment"] },
  { to: "/hub/you", label: "You", icon: User, match: ["/hub/you", "/profile", "/settings", "/dashboard", "/refer", "/affiliate", "/leaderboard", "/admin", "/opportunities", "/resume", "/interview", "/journal"] },
] as const;

/** Kept for reference: every destination here now lives inside a hub. */
export const SHELL_MORE = [
  { to: "/next-steps", label: "Next Step", icon: ListChecks, hint: "The 5 things to do this month" },
  { to: "/careers", label: "Careers", icon: Briefcase, hint: "Explore all 5,000+ roles" },
  { to: "/vision-board", label: "Vision Board", icon: Image, hint: "Pin the life you want" },
  { to: "/skills", label: "Skills", icon: Sparkles, hint: "What to build next" },
  { to: "/command-center", label: "Command Center", icon: Rocket, hint: "Your future, planned" },
  { to: "/dashboard", label: "Tools & History", icon: LayoutGrid, hint: "Every tool, past attempts, rewards" },
] as const;

type Props = {
  children: ReactNode;
  /** Short screen title shown in the mobile header. */
  title?: string;
  /** Hide the floating mentor button on screens that own the full canvas. */
  hideMentor?: boolean;
};

export default function AppShell({ children, title, hideMentor }: Props) {
  const { pathname } = useLocation();
  const [mentorOpen, setMentorOpen] = useState(false);
  const inArea = (match: readonly string[]) =>
    match.some((m) => pathname === m || pathname.startsWith(`${m}/`));
  const careersActive = pathname === "/careers" || pathname.startsWith("/careers/");

  const railItem = (active: boolean) =>
    `flex min-h-12 items-center gap-3 rounded-2xl px-3 text-[15px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
      active ? "bg-primary/12 text-primary" : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
    }`;

  const tab = (a: (typeof SHELL_AREAS)[number]) => (
    <li key={a.to} className="flex-1">
      <NavLink
        to={a.to}
        aria-current={inArea(a.match) ? "page" : undefined}
        className={`flex min-h-[60px] flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${
          inArea(a.match) ? "text-primary" : "text-muted-foreground"
        }`}
      >
        <a.icon className="h-5 w-5" aria-hidden="true" />
        <span className="truncate px-0.5">{a.label}</span>
      </NavLink>
    </li>
  );

  return (
    <div className="min-h-dvh bg-background">
      {/* Desktop rail */}
      <aside
        className="print:hidden hidden lg:flex fixed inset-y-0 left-0 z-40 w-[248px] flex-col gap-8 border-r border-border/50 bg-[hsl(var(--surface))] px-4 py-7"
        aria-label="Main navigation"
      >
        <Link to="/home" className="flex items-center gap-2.5 px-2">
          <WeKitLogo size="sm" />
          <span className="font-display text-lg font-bold tracking-tight">WeKIT™</span>
        </Link>

        <Link
          to="/careers"
          aria-current={careersActive ? "page" : undefined}
          className="flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-primary px-4 text-[15px] font-semibold text-primary-foreground shadow-md shadow-primary/20 transition-transform hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <Briefcase className="h-[18px] w-[18px]" aria-hidden="true" /> Explore careers
        </Link>

        <nav className="flex flex-col gap-1.5">
          {SHELL_AREAS.map((a) => (
            <NavLink key={a.to} to={a.to} aria-current={inArea(a.match) ? "page" : undefined} className={railItem(inArea(a.match))}>
              <a.icon className="h-[18px] w-[18px]" aria-hidden="true" />
              {a.label === "DNA™" ? "Career DNA™" : a.label}
            </NavLink>
          ))}
          {!hideMentor && (
            <button type="button" onClick={() => setMentorOpen(true)} aria-expanded={mentorOpen} className={railItem(false)}>
              <MessageCircle className="h-[18px] w-[18px] text-ai" aria-hidden="true" /> Ask YoMA
            </button>
          )}
        </nav>
      </aside>

      {/* Mobile header */}
      <header className="print:hidden lg:hidden sticky top-0 z-40 flex h-14 items-center gap-3 border-b border-border/50 bg-background/85 px-4 backdrop-blur-xl">
        <Link
          to="/home"
          aria-label="WeKIT home"
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <WeKitLogo size="sm" clickable={false} />
        </Link>
        <h1 className="flex-1 truncate font-display text-base font-semibold tracking-tight">
          {title ?? "WeKIT™"}
        </h1>
        <Link
          to="/careers"
          aria-label="Explore careers"
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <Search className="h-5 w-5" aria-hidden="true" />
        </Link>
      </header>

      <main className="lg:pl-[248px] print:pl-0">
        <div className="mx-auto w-full max-w-3xl px-4 pb-32 pt-6 lg:max-w-4xl lg:px-10 lg:pb-16 lg:pt-10">
          {children}
        </div>
      </main>

      {!hideMentor && <MentorPanel open={mentorOpen} onClose={() => setMentorOpen(false)} />}

      {/* Mobile tab bar — YoMA sits in the centre */}
      <nav
        className="print:hidden lg:hidden fixed inset-x-0 bottom-0 z-40 border-t border-border/50 bg-background/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl"
        aria-label="Main navigation"
      >
        <ul className="flex items-stretch">
          {SHELL_AREAS.slice(0, 2).map(tab)}
          <li className="flex flex-1 items-start justify-center">
            <button
              type="button"
              onClick={() => (hideMentor ? undefined : setMentorOpen(true))}
              aria-label="Ask YoMA, your mentor"
              aria-expanded={mentorOpen}
              disabled={hideMentor}
              className="-mt-5 flex h-14 w-14 flex-col items-center justify-center rounded-full bg-ai text-ai-foreground shadow-lg shadow-ai/30 transition-transform active:scale-95 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <MessageCircle className="h-5 w-5" aria-hidden="true" />
              <span className="text-[10px] font-semibold">YoMA</span>
            </button>
          </li>
          {SHELL_AREAS.slice(2).map(tab)}
        </ul>
      </nav>
    </div>
  );
}
